import { Modal, useTheme } from '@fluentui/react';
import { FC, FCWithChildren, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { TemporaryDataCacheContext } from '../../providers/TemporaryDataCacheProvider';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import Grid from '../../components/shared/Grid';
import { ModalHeader } from '../../shared/styled-components';
import { IQueueReport } from '../../models/reports.internal';
import { defaultIQueueKpiThresholds } from '../../models/reports.thresholds';
import { MediaGroup } from '../../models/workflow';
import { useGetQueueKpiThresholdsQuery } from '../../redux/apis/reportingThresholdsApi';
import Card from '../../components/shared/Card';
import ExpandToggle from '../../components/shared/ExpandToggle';
import Report from '../../components/shared/Report';
import ClockDisplay from '../../components/queues/ClockDisplay';
import ElapsedTimeClockDisplay from '../../components/queues/ElapsedTimeClockDisplay';
import SlaDisplay from '../../components/queues/SlaDisplay';
import { AbandonedIcon, CompletedIcon, ConversationsConnected, ConversationsWaiting, QueueTimedOut } from '../../components/shared/icons';
import useCompletedCount from '../../shared/hooks/useCompletedCount';
import AbandonedConversations from '../../components/queues/AbandonedConversations';
import CompletedConversations from '../../components/queues/CompletedConversations';
import ConnectedConversations from '../../components/queues/ConnectedConversations';
import WaitingConversations from '../../components/queues/WaitingConversations';

const ListContainer = styled.div`
  min-height: 1px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  .bep-ListDiv {
    overflow: auto;
  }

  .ms-Viewport, .ms-DetailsList {
    overflow: visible !important;
  }
`;

function getIconFromMediaGroup(mediaGroup: MediaGroup | null): string | undefined {
  switch (mediaGroup) {
    case 'chat':
      return 'CannedChat';
    case 'email':
      return 'Mail';
    case 'voice':
      return 'Phone';
    case 'virtual':
      return 'Flag';
    default:
      return undefined;
  }
}

interface IListModalProps {
  isOpen: boolean;
  title: string;
  onDismiss(): void;
}

const ListModal: FCWithChildren<IListModalProps> = ({ children, isOpen, title, onDismiss }) => (
  <Modal
    isOpen={isOpen}
    onDismiss={onDismiss}
    isBlocking={false}
  >
    <ModalHeader title={title} onCancel={onDismiss} />
    <ListContainer>
      {children}
    </ListContainer>
  </Modal>
);

const lwtFullness = (lwtSeconds: number, threshold: number | null | undefined): number => {
  if (!threshold)
    return lwtSeconds ? 1 : 0;

  return Math.min(1, lwtSeconds / threshold);
};

const lwtFullnessFn = (threshold: number | null | undefined) =>
  (lwtSeconds: number) => lwtFullness(lwtSeconds, threshold);

interface IProps {
  num: number;
  report: IQueueReport;
}

const QueueReport: FC<IProps> = ({ num, report }) => {
  const { storeData, retrieveData } = useContext(TemporaryDataCacheContext);

  const { palette } = useTheme();
  const [shownList, setShownList] = useState<'Waiting' | 'Connected' | 'Completed' | 'Abandoned' | null>(null);

  const [expanded, setExpanded] = useState(retrieveData<boolean>(`QueueReport-${report.id}-expanded`) ?? false);
  const updateExpanded = useCallback(
    (value: boolean) => {
      setExpanded(value);
      storeData(`QueueReport-${report.id}-expanded`, value, 'session');
    },
    [report.id, storeData],
  );

  const { data: thresholds = defaultIQueueKpiThresholds } = useGetQueueKpiThresholdsQuery(report.id);

  const completedCount = useCompletedCount(report.acceptedCount, report.conversationsConnected.length);
  const { formatTimeSpan } = useTimeFormatters();

  const slaColor = useCallback(
    (slaRatio: number, threshold: number | null | undefined): string => {
      if (!threshold)
        return palette.green;

      return slaRatio >= threshold
        ? palette.green
        : palette.redDark;
    },
    [palette],
  );

  const lwtColor = useCallback(
    (lwtSeconds: number, threshold: number | null | undefined): string => {
      if (!threshold)
        return palette.green;

      return lwtSeconds < threshold
        ? palette.green
        : palette.redDark;
    },
    [palette],
  );

  const lwtColorFn = useCallback(
    (threshold: number | null | undefined) => (lwtSeconds: number) => lwtColor(lwtSeconds, threshold),
    [lwtColor],
  );

  const templateAreaRows = expanded
    ? [
      'times '.repeat(2) + 'conversations '.repeat(4),
      'waitTime '.repeat(3) + 'callback '.repeat(3),
    ]
    : ['times '.repeat(2) + 'conversations '.repeat(4)];

  return (
    <Report num={num}
      iconName={getIconFromMediaGroup(report.mediaGroup)}
      text={report.name}
      onRenderText={undefined}
      showEdit={false}
      onEditClick={undefined}
      tabs={undefined}
      extraControl={<ExpandToggle isExpanded={expanded} onClick={() => updateExpanded(!expanded)} />}
    >
      <Grid rows={{ mode: 'auto', size: 'auto' }} columns={{ mode: 'equal' }} templateAreaRows={templateAreaRows} gap="0.5rem">
        <Card gridArea="times"
          showSeparator
          items={[
            {
              title: 'Service Level',
              content: <SlaDisplay percent={Math.round(report.slaRatio * 100)} color={slaColor(report.slaRatio, thresholds.slaThreshold)} />,
            },
            {
              title: 'Current LWT',
              content: <ElapsedTimeClockDisplay timeStart={report.lwtStart} colorFn={lwtColorFn(thresholds.maxLwtSeconds)} fullnessFn={lwtFullnessFn(thresholds.maxLwtSeconds)} />,
            },
          ]}
        />
        <Card gridArea="conversations"
          showSeparator
          items={[
            {
              title: 'Waiting',
              // eslint-disable-next-line react/display-name
              onExtrasButtonClick: () => setShownList('Waiting'),
              extrasButtonIcon: 'Zoom',
              extrasButtonTooltip: 'View waiting conversations',
              content: <ConversationsWaiting text={report.conversationsWaiting.length} thresholdMet={report.conversationsWaiting.length < (thresholds.maxConversationsWaiting ?? Infinity)} />,
            },
            {
              title: 'Connected',
              // eslint-disable-next-line react/display-name
              onExtrasButtonClick: () => setShownList('Connected'),
              extrasButtonIcon: 'Zoom',
              extrasButtonTooltip: 'View connected conversations',
              content: <ConversationsConnected text={report.conversationsConnected.length} />,
            },
            {
              title: 'Completed',
              // eslint-disable-next-line react/display-name
              onExtrasButtonClick: () => setShownList('Completed'),
              extrasButtonIcon: 'Zoom',
              extrasButtonTooltip: 'View completed conversations',
              content: <CompletedIcon text={completedCount} />,
            },
            {
              title: 'Abandoned',
              // eslint-disable-next-line react/display-name
              onExtrasButtonClick: () => setShownList('Abandoned'),
              extrasButtonIcon: 'Zoom',
              extrasButtonTooltip: 'View abandoned conversations',
              content: <AbandonedIcon text={report.abandonedCount} />,
            },
          ]}
        />
        {
          expanded &&
          <>
            <Card gridArea="waitTime"
              title="Wait Time Today"
              items={[
                {
                  content: <ClockDisplay text={formatTimeSpan(report.longestWaitTimeInSeconds)} color={lwtColor(report.longestWaitTimeInSeconds, thresholds.maxLwtSeconds)} fullness={lwtFullness(report.longestWaitTimeInSeconds, thresholds.maxLwtSeconds)} />,
                  label: 'Longest',
                },
                {
                  content: <ClockDisplay text={formatTimeSpan(report.avgWaitingTimeInSeconds)} color={lwtColor(report.avgWaitingTimeInSeconds, thresholds.averageWaitTimeSeconds)} fullness={lwtFullness(report.avgWaitingTimeInSeconds, thresholds.averageWaitTimeSeconds)} />,
                  label: 'Average',
                },
              ]}
            />
            <Card gridArea="callback"
              showSeparator
              items={[
                {
                  title: 'Timed Out',
                  content: <QueueTimedOut text={report.queueTimedOutCount} />,
                },
              ]}
            />
          </>
        }
      </Grid>
      <ListModal title={`${shownList ?? ''} conversations`} isOpen={!!shownList} onDismiss={() => setShownList(null)}>
        {
          {
            'Waiting': <WaitingConversations items={report.conversationsWaiting} />,
            'Connected': <ConnectedConversations items={report.conversationsConnected} />,
            'Completed': <CompletedConversations queueId={report.id} startOfToday={report.startOfToday} />,
            'Abandoned': <AbandonedConversations queueId={report.id} startOfToday={report.startOfToday} />,
            '': null,
          }[shownList ?? '']
        }
      </ListModal>
    </Report>
  );
};

export default QueueReport;